/**
 * Phase L — /partners/ivy-league-consulting
 *
 * Co-branded partner page. Renders 404 if site_settings.ilc_partnership_visible=false.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";
import IvyLeagueConsultingLogo from "@/components/IvyLeagueConsultingLogo";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const PARTNER_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Beyond Valencia",
    url: "https://ivyleagueconsulting.org",
    description: "Florida transfer admissions strategy — community college to UF, UCF, FSU, and America's most selective universities.",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Ivy League Consulting",
    url: "https://ivyleagueconsulting.org",
    description: "Elite / Ivy admissions strategy for transfer applicants. Beyond Valencia's partner for out-of-state and top-30 destinations.",
  },
];

export default function PartnersILC() {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    axios.get(`${API}/partners/ivy-league-consulting`)
      .then((r) => setData(r.data))
      .catch((err) => setNotFound(err?.response?.status === 404));
  }, []);

  if (notFound) {
    return (
      <div className="min-h-screen bg-bone">
        <SEO noindex title="Partnership paused" canonical="/partners/ivy-league-consulting" />
        <TopNav />
        <main className="max-w-3xl mx-auto px-5 py-24">
          <h1 className="font-serif text-4xl text-ivy">Partnership currently paused</h1>
          <p className="mt-4 text-ink-soft">This page is temporarily hidden. Please check back later.</p>
        </main>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-bone">
        <TopNav />
        <div className="max-w-4xl mx-auto px-5 py-24 text-ink-soft">Loading partnership details…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="partners-ilc-page">
      <SEO
        title="Ivy League Consulting Partnership | Beyond Valencia"
        description="Beyond Valencia is the Florida transfer engine. Ivy League Consulting handles elite and Ivy destinations. Together we cover any US university transfer pathway."
        canonical="/partners/ivy-league-consulting"
        ogType="article"
        jsonLd={PARTNER_JSONLD}
      />
      <TopNav />

      <main>
        {/* Hero */}
        <section className="bg-ivy text-bone" data-testid="partners-ilc-hero">
          <div className="max-w-5xl mx-auto px-5 py-16 sm:py-24">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Partnership</div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05] tracking-tight" data-testid="partners-ilc-h1">
              The Florida transfer engine,<br />paired with elite admissions strategy.
            </h1>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">
                Beyond Valencia<br /><span className="text-bone-dim normal-case tracking-normal text-xs">Florida transfer specialization</span>
              </div>
              <span className="text-brass-soft text-2xl">×</span>
              <IvyLeagueConsultingLogo variant="light" />
            </div>
          </div>
        </section>

        {/* Two-column split */}
        <section className="max-w-5xl mx-auto px-5 py-16">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="border-l-2 border-brass pl-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Beyond Valencia handles</div>
              <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">Florida transfer specialization</h2>
              <ul className="mt-5 space-y-3 text-sm text-ink-soft leading-relaxed" data-testid="partners-ilc-bv-role">
                {data.beyond_valencia_role.map((r, i) => (
                  <li key={i} className="flex gap-3"><span className="text-brass">•</span>{r}</li>
                ))}
              </ul>
            </div>
            <div className="border-l-2 border-ivy pl-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Ivy League Consulting handles</div>
              <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">Elite &amp; Ivy admissions strategy</h2>
              <ul className="mt-5 space-y-3 text-sm text-ink-soft leading-relaxed" data-testid="partners-ilc-ilc-role">
                {data.ilc_role.map((r, i) => (
                  <li key={i} className="flex gap-3"><span className="text-brass">•</span>{r}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Referral flow */}
        <section className="bg-bone-dim/60 border-y border-stone/30">
          <div className="max-w-4xl mx-auto px-5 py-16" data-testid="partners-ilc-referrals">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">How referrals work</div>
            <h2 className="mt-2 font-serif text-3xl text-ivy tracking-tight">Honest, additive routing.</h2>
            <ol className="mt-6 space-y-4 text-ink-soft leading-relaxed">
              {data.referral_flow.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-serif text-2xl text-brass tabular-nums w-8 shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-5 py-16 text-center">
          <h2 className="font-serif text-3xl text-ivy tracking-tight">Ready to start?</h2>
          <p className="mt-3 text-ink-soft text-sm max-w-xl mx-auto">
            The intake wizard routes you to the right team based on your target list. All Florida in-state targets stay with Beyond Valencia; any Ivy, elite, or out-of-state target routes to our partner.
          </p>
          <Link
            to="/build-my-plan"
            className="mt-6 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 hover:bg-brass hover:border-brass"
            data-testid="partners-ilc-intake-cta"
          >
            Start your intake →
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
